'use client';

import { useMemo, useSyncExternalStore, type ComponentPropsWithoutRef, type ElementType } from 'react';
import { htmlToPlainText, sanitizeHtml, type SanitizeProfile } from '@/lib/security/sanitizeHtml';

const noopSubscribe = () => () => {};
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

type SafeHtmlProps<T extends ElementType> = {
  /** Untrusted HTML (organizer/user authored). */
  html: string | null | undefined;
  profile?: SanitizeProfile;
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, 'children' | 'dangerouslySetInnerHTML'>;

/**
 * Renders untrusted HTML through the shared sanitizer.
 *
 * On the server (and during the hydration pass, so both sides match) it
 * renders only the plain text of the HTML as a React text child – escaped by
 * React, never markup. Once on the client it swaps in the DOMPurify-sanitized
 * markup. Client-side navigations render the sanitized markup directly.
 */
export default function SafeHtml<T extends ElementType = 'div'>({
  html,
  profile = 'rich',
  as,
  ...rest
}: SafeHtmlProps<T>) {
  const Tag = (as ?? 'div') as ElementType;
  const isClient = useSyncExternalStore(noopSubscribe, getClientSnapshot, getServerSnapshot);

  const clean = useMemo(() => (isClient ? sanitizeHtml(html, profile) : null), [isClient, html, profile]);

  if (clean === null) {
    return <Tag {...rest}>{htmlToPlainText(html ?? '')}</Tag>;
  }
  return <Tag {...rest} dangerouslySetInnerHTML={{ __html: clean }} />;
}
